import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faClock,
  faChevronLeft,
  faUsers,
  faEye,
  faComment,
  faEllipsisV,
  faUser,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatTime, formatVisibility, getGradient } from '../utils/eventHelpers';
import { fixImageUrl } from '../../../../services/imageService';
import LikeButton from '../../../LikeButton';

/**
 * Header component for the event view
 * 
 * @param {object} event - Event data object
 * @param {number} scrollY - Current scroll position
 * @param {function} onNavigateBack - Function to handle back navigation
 * @param {function} onOptionsClick - Function to handle options button click
 * @returns {JSX.Element} Event header component
 */
const EventHeader = ({
  event,
  scrollY,
  onNavigateBack,
  onOptionsClick
}) => {
  const { t } = useLanguage();
  const [showDescription, setShowDescription] = useState(false);
  const optionsButtonRef = useRef(null);

  // Calculate header styles based on scroll position
  const headerScale = Math.max(0.8, 1 - scrollY / 350);
  const headerOpacity = Math.max(0.4, 1 - scrollY / 250);
  const headerBorderRadius = Math.min(20, (scrollY / 5));

  // Format event date for display
  const eventDate = new Date(event.date);
  const monthAbbr = eventDate.toLocaleDateString('sl-SI', { month: 'short' });
  const dayNumber = eventDate.getDate();
  const dayOfWeek = eventDate.toLocaleDateString('sl-SI', { weekday: 'short' });

  // Format community name
  const communityName = event.community_name || '';

  // Get header background style - either image or gradient
  const getHeaderBackground = () => {
    if (event.has_header_image) {
      // Add the /api prefix to match the backend route
      const headerImageUrl = fixImageUrl(`/api/events/${event.id}/header-image`);
      
      // Log the URL for debugging
      
      return {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url(${headerImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }
    return { backgroundImage: getGradient(event) };
  };

  // Handle options button click with proper event data
  const handleOptionsClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onOptionsClick) {
      onOptionsClick(e);
    }
  };

  return (
    <>
      {/* Back button - fixed position */}
      <button className="event-back-button" onClick={onNavigateBack}>
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>

      {/* Header with background (gradient or image) that scales when scrolling */}
      <div 
        className="event-header-container" 
        style={getHeaderBackground()}
      >
        <div 
          className="event-header-content"
          style={{
            transform: `scale(${headerScale})`,
            opacity: headerOpacity,
            borderRadius: scrollY > 0 ? `0 0 ${headerBorderRadius}px ${headerBorderRadius}px` : '0'
          }}
        >
          {/* Date display */}
          <div className="event-date-display">
            <div className="event-date-block">
              <div className="month">{monthAbbr}</div>
              <div className="day">{dayNumber}</div>
            </div>
          </div>

          {/* Combined title and details */}
          <div className="event-info">
            <div className="d-flex justify-content-between align-items-start w-100">
              <h1 className="event-title">{event.title}</h1>
              
              {/* Options button with explicit styling */}
              <div className="event-options-button-container">
                <button 
                  ref={optionsButtonRef}
                  className="btn btn-icon text-white p-0"
                  onClick={handleOptionsClick}
                  aria-label="Možnosti dogodka"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '8px',
                    cursor: 'pointer',
                    zIndex: 100 // Ensure it's clickable
                  }}
                >
                  <FontAwesomeIcon icon={faEllipsisV} size="lg" />
                </button>
              </div>
            </div>
            
            {/* Location and time side by side */}
            <div className="event-info-details">
              <div className="d-flex flex-row align-items-center">
                {event.location && (
                  <div className="event-location me-3">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="event-icon" />
                    {event.location}
                  </div>
                )}
                <div className="event-time">
                  <FontAwesomeIcon icon={faClock} className="event-icon" />
                  {dayOfWeek} {formatTime(event.time)}
                </div>
              </div>
              
              {/* Add organizer info */}
              {event.owner_display_name && (
                <div className="event-organizer mt-1">
                  <FontAwesomeIcon icon={faUser} className="event-icon" />
                  {t('events.attendees.organizer')}: {event.owner_display_name}
                </div>
              )}
            </div>
            
            {/* Community badge, visibility, and recording link */}
            <div className="event-meta">
              {communityName && (
                <span className="event-community">
                  <FontAwesomeIcon icon={faUsers} className="event-icon" />
                  {communityName}
                </span>
              )}
              {event.visibility && (
                <span className="event-visibility">
                  <FontAwesomeIcon icon={faEye} className="event-icon" />
                  {formatVisibility(event.visibility)}
                </span>
              )}
              {event.youtube_link && (
                <span
                  className="event-recording"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(event.youtube_link, '_blank', 'noopener,noreferrer');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon={faVideo} className="event-icon" />
                  {t('events.modals.details.videoLink')}
                </span>
              )}
            </div>
            
            {/* Description toggle */}
            {event.description && (
              <>
                <div
                  className="event-description-toggle"
                  onClick={() => setShowDescription(!showDescription)}
                >
                  <FontAwesomeIcon icon={faComment} className="event-icon" />
                  <span>{showDescription ? t('events.hideDescription') || 'Hide description' : t('events.showDescription') || 'Show description'}</span>
                </div>

                {/* Collapsible description section */}
                {showDescription && (
                  <div className="event-description">
                    {event.description}
                  </div>
                )}
              </>
            )}

            {/* Like button — last element in header */}
            <div className="mt-2" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <LikeButton contentType="event" contentId={event?.id} theme="light" showLikedByText />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EventHeader;